import { IMPORT_SLICE, ImportReport } from '@itc/shared';
import { z } from 'zod';
import { ARCHIVE_KINDS, ArchiveImportRow } from './archive.ts';
import { SampleImportRow, SnapshotImportRow } from './history.ts';
import { REPORTS, VIEW_REPORTS } from './reports.ts';
import { SPANS } from './samples.ts';

// The shapes of Members' reports, charts and archive (plan 5F), used by the server and the browser. A report or a history
// is made per tier (design §6.12): what a tier may not see is never in its answer.

export const ReportParam = z.enum(REPORTS);
/** The three reports `members.view` has. The threat report has its own route, for `members.manage`. */
export const ViewReportParam = z.enum(VIEW_REPORTS);

/** A report as a table, and how it was made. */
export const ReportView = z.object({
  name: ReportParam,
  title: z.string(),
  about: z.string(),
  tier: z.enum(['view', 'manage']),
  /** When the guild was read, which is up to a minute before the request: one read is shared. */
  guildReadAt: z.string(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  summary: z.array(z.object({ label: z.string(), count: z.number().int() })),
  notes: z.array(z.string()),
});
export type ReportView = z.infer<typeof ReportView>;

/** A CSV made on the server at the caller's tier, and the name to save it as. */
export const CsvFile = z.object({ filename: z.string(), csv: z.string() });
export type CsvFile = z.infer<typeof CsvFile>;

// --- The org's numbers over time ---------------------------------------------------------

const HistoryCommon = z.object({
  at: z.string(),
  rsiMembers: z.number().int(),
  linkedMembers: z.number().int(),
  verifiedMembers: z.number().int(),
  typeMember: z.number().int(),
  typeAffiliate: z.number().int(),
});
const HistoryView = z.object({ tier: z.literal('view'), points: z.array(HistoryCommon) });
const HistoryManage = z.object({ tier: z.literal('manage'), points: z.array(HistoryCommon.extend({ threatMembers: z.number().int(), redactedOrgMembers: z.number().int() })) });
/** Snapshots, oldest first. The threat numbers are for members.manage. */
export const History = z.discriminatedUnion('tier', [HistoryView, HistoryManage]);
export type History = z.infer<typeof History>;

// --- Role counts -------------------------------------------------------------------------

export const RoleCountsQuery = z.object({ span: z.enum(SPANS).default('day') });
export type RoleCountsQuery = z.input<typeof RoleCountsQuery>;

export const RoleCountsView = z.object({
  span: z.enum(SPANS),
  /** The moment the buckets are counted from. */
  now: z.string(),
  /** Whether any role is tracked: the setting is empty until an admin picks some. */
  tracked: z.boolean(),
  retentionDays: z.number().int(),
  /** Whether the roles' names could be read. Without them a role is shown by its id. */
  named: z.boolean(),
  roles: z.array(
    z.object({
      roleId: z.string(),
      name: z.string(),
      points: z.array(z.object({ at: z.string(), count: z.number().int().nullable() })),
      /** The mean of what is plotted, leaving out gaps. */
      average: z.number().nullable(),
    }),
  ),
});
export type RoleCountsView = z.infer<typeof RoleCountsView>;

// --- The archive -------------------------------------------------------------------------

const Filter = z.string().max(500).default('');

export const ArchiveQuery = z.object({
  kind: z.enum(ARCHIVE_KINDS).default('action_log'),
  /** A log's category, severity and outcome; a command's status is its outcome, and its type its action. All exact, as the old console's were. */
  category: Filter,
  severity: Filter,
  outcome: Filter,
  actor: Filter,
  target: Filter,
  action: Filter,
  page: z.string().regex(/^[0-9]{1,6}$/).optional(),
});
export type ArchiveQuery = z.input<typeof ArchiveQuery>;
/** The query as the server reads it, with its defaults filled in. */
export type ArchiveFilters = z.output<typeof ArchiveQuery>;

export const ArchiveRow = z.object({
  id: z.string(),
  kind: z.enum(ARCHIVE_KINDS),
  oldId: z.string(),
  at: z.string().nullable(),
  category: z.string(),
  severity: z.string(),
  outcome: z.string(),
  actor: z.string(),
  target: z.string(),
  action: z.string(),
  /** The row as it was. */
  data: z.unknown(),
});
export type ArchiveRow = z.infer<typeof ArchiveRow>;

export const ArchivePage = z.object({
  kind: z.enum(ARCHIVE_KINDS),
  items: z.array(ArchiveRow),
  page: z.number().int(),
  perPage: z.number().int(),
  totalItems: z.number().int(),
  totalPages: z.number().int(),
  /** How many rows of each kind are archived, ignoring the filters. */
  counts: z.record(z.enum(ARCHIVE_KINDS), z.number().int()),
});
export type ArchivePage = z.infer<typeof ArchivePage>;

// --- The imports -------------------------------------------------------------------------

/** What a history import tells the page about the server it is for. */
export const HistoryImportContext = z.object({ guildId: z.string().nullable(), retentionDays: z.number().int() });
export type HistoryImportContext = z.infer<typeof HistoryImportContext>;

/** ADR 0019's report, and the ids of the rows it created (or, for a dry run, would). */
export const HistoryImportReport = ImportReport.extend({ creating: z.array(z.string()) });
export type HistoryImportReport = z.infer<typeof HistoryImportReport>;

/** The keys of earlier slices' rows a dry run would create, so this slice can find the repeats among them. Refused for an import. */
export const EarlierKeys = z.array(z.string().max(260)).max(200).optional();

const importInput = <Row extends z.ZodType>(row: Row) =>
  z.object({
    dryRun: z.boolean(),
    rows: z.array(row).min(1).max(IMPORT_SLICE),
    earlier: EarlierKeys,
  });
export const SnapshotImportInput = importInput(SnapshotImportRow);
export const SampleImportInput = importInput(SampleImportRow);
export const ArchiveImportInput = importInput(ArchiveImportRow);
export type SnapshotImportInput = z.infer<typeof SnapshotImportInput>;
export type SampleImportInput = z.infer<typeof SampleImportInput>;
export type ArchiveImportInput = z.infer<typeof ArchiveImportInput>;
